import { useCallback, useEffect, useMemo, useState } from 'react';

import { SortOrder, SortValueType, sortRows } from '../../../../utils/sortUtils';

/**
 * AF-3418: ordenação + paginação + busca para tabelas cujos dados já estão em memória.
 *
 * As telas do recommendation-engine carregam o lote inteiro no cliente (não há endpoint
 * paginado de recomendações), então o padrão server-side do monitoramento não se aplica.
 * Este hook replica o mesmo comportamento de interação (toggle ASC/DESC, reset de página
 * ao ordenar/filtrar) usando o comparador tipado de sortUtils.
 *
 * Também resolve um problema de renderização: sem paginação a tela montava todas as linhas
 * de uma vez - um lote de 661 documentos virava 661 linhas no DOM, e lotes ESCAVADOR
 * passam de mil.
 */
export interface UseClientTableOptions<T> {
    rows: T[];
    /** Tipo de cada coluna ordenável, para o comparador escolher a estratégia certa. */
    columnTypes?: Record<string, SortValueType>;
    /** Lê o valor de ordenação quando ele não é uma propriedade direta da linha. */
    valueAccessor?: (row: T, field: string) => unknown;
    /** Campos considerados na busca textual livre. */
    searchableFields?: (row: T) => string[];
    /** Predicados adicionais de filtro (ex.: recomendação, sinalizado crédito). */
    filterFn?: (row: T) => boolean;
    initialPageSize?: number;
    initialSortBy?: string;
    initialSortOrder?: SortOrder;
}

export interface UseClientTableResult<T> {
    /** Linhas da página atual, já filtradas e ordenadas. */
    pageRows: T[];
    /** Todas as linhas filtradas e ordenadas (sem cortar a página). */
    filteredRows: T[];
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    totalPages: number;
    totalRows: number;
    /** Total antes dos filtros, para exibir "X de Y". */
    unfilteredTotal: number;
    sortBy: string;
    sortOrder: SortOrder;
    toggleSort: (field: string) => void;
    search: string;
    setSearch: (term: string) => void;
    rangeStart: number;
    rangeEnd: number;
}

export default function useClientTable<T>({
    rows,
    columnTypes = {},
    valueAccessor,
    searchableFields,
    filterFn,
    initialPageSize = 25,
    initialSortBy = '',
    initialSortOrder = 'ASC',
}: UseClientTableOptions<T>): UseClientTableResult<T> {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(initialPageSize);
    const [sortBy, setSortBy] = useState(initialSortBy);
    const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
    const [search, setSearchState] = useState('');

    const normalizedSearch = search.trim().toLowerCase();

    const filteredRows = useMemo(() => {
        let result = rows;

        if (filterFn) {
            result = result.filter(filterFn);
        }

        if (normalizedSearch && searchableFields) {
            result = result.filter((row) =>
                searchableFields(row).some((value) =>
                    (value ?? '').toString().toLowerCase().includes(normalizedSearch)
                )
            );
        }

        return sortRows(result, sortBy, sortOrder, columnTypes, valueAccessor);
        // columnTypes/valueAccessor/searchableFields/filterFn são recriados a cada render
        // nas telas; comparar por identidade recalcularia sempre. As dependências abaixo
        // cobrem o que de fato muda o resultado.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, sortBy, sortOrder, normalizedSearch, filterFn]);

    const totalRows = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

    // Mantém a página dentro do intervalo válido quando o filtro reduz o total
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const pageRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, page, pageSize]);

    const toggleSort = useCallback((field: string) => {
        setSortBy((currentField) => {
            if (currentField === field) {
                setSortOrder((currentOrder) => (currentOrder === 'ASC' ? 'DESC' : 'ASC'));
                return currentField;
            }
            setSortOrder('ASC');
            return field;
        });
        setPage(1);
    }, []);

    const setSearch = useCallback((term: string) => {
        setSearchState(term);
        setPage(1);
    }, []);

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
        setPage(1);
    }, []);

    const rangeStart = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, totalRows);

    return {
        pageRows,
        filteredRows,
        page,
        setPage,
        pageSize,
        setPageSize,
        totalPages,
        totalRows,
        unfilteredTotal: rows.length,
        sortBy,
        sortOrder,
        toggleSort,
        search,
        setSearch,
        rangeStart,
        rangeEnd,
    };
}
